/**
 * enemy-registry.ts — 100 種敵人(bestiary)的資料驅動登錄。
 *
 * boss 2026-06-17 目標:把 100 種新敵人接進遊戲、行為差異化、掉落差異化。
 * 設計來源:docs/spec/ENEMY_BESTIARY.md(20 角色機制網格 × 5 門派環境)。
 *
 * 這支是純資料/純函式(Phaser-free,可單測):把 bestiary JSON 轉成帶「行為旗標 +
 * 戰鬥數值 + 掉落」的 EnemyDef,供 scene 生成/行為/render/死亡結算讀。
 *
 * ⚠️ 雙軌鐵則:掉落只給「玩遊戲軌」——局內 coins / 局內 XP(_runXp)/ 局內 pickup。
 * 絕不在此定義 factionXP / freeXP / skillLevels(learning-game-separation.test 守)。
 */
import type { FactionId } from '../../shared/types';
import bestiary from '../data/enemy-bestiary.json';

export type EnemyRole =
  | 'grunt' | 'scout' | 'heavy' | 'swarm-sine' | 'swarm-chase' | 'sniper' | 'bomber'
  | 'shielder' | 'summoner' | 'buffer' | 'striker' | 'spread' | 'ring' | 'beam'
  | 'homing' | 'miner' | 'blinker' | 'splitter' | 'healer' | 'elite';

export type EnemyChassis = 'tank' | 'plane' | 'swarm' | 'monster' | 'turret' | 'walker';
export type EnemyMove =
  | 'straight' | 'sine' | 'diagonal' | 'chaseY' | 'chase' | 'stationary' | 'strafing' | 'blink';
/** 攻擊投射物種類(對映 enemy-projectiles.json 的 proj-<faction>-<type> 圖)。 */
export type AttackKind =
  | 'none' | 'bolt' | 'shell' | 'lance' | 'bomb' | 'beam' | 'missile' | 'mine' | 'flame';
/** 局內可撿道具(全部純局內、不寫 progress)。 */
export type PickupKind = 'heal' | 'rage' | 'coinburst' | 'powerup' | 'magnet';

/** bestiary JSON 一筆的形狀。 */
export interface EnemyDef {
  id: string;
  faction: FactionId;
  name_zh: string;
  name_en: string;
  role: EnemyRole;
  chassis: EnemyChassis;
  hp_tier: number;       // 1(最低)..5(最高)
  speed: 'slow' | 'med' | 'fast';
  move: EnemyMove;
  attack: string;        // 中文敘述(設計用)
  swarm: number;         // 0=單;>0=成群數量
  summon: string | null; // 召喚的 enemy id,或「死亡分裂×N」之類文字,或 null
  signature: string;
  art_prompt: string;
}

/** role → 行為旗標 + 戰鬥/掉落調性。差異化的核心表。 */
export interface RoleMeta {
  attack: AttackKind;        // 發射的投射物(none=不發射/近戰/支援)
  contact: boolean;          // 近戰接觸傷(striker / swarm-chase)
  canSummon: boolean;        // 週期召喚(summoner / elite)
  hasShield: boolean;        // 正面護盾擋彈(shielder)
  buffsAllies: boolean;      // 範圍 buff 友軍(buffer)
  healsAllies: boolean;      // 範圍治療友軍(healer)
  splitsOnDeath: boolean;    // 死亡分裂(splitter)
  /** 掉落(玩遊戲軌):金幣倍率、局內 XP 倍率、可撿 pickup 與機率。 */
  coinMult: number;
  xpMult: number;
  pickup: PickupKind | null;
  pickupChance: number;      // 0..1
}

const R = (m: Partial<RoleMeta>): RoleMeta => ({
  attack: 'bolt', contact: false, canSummon: false, hasShield: false,
  buffsAllies: false, healsAllies: false, splitsOnDeath: false,
  coinMult: 1, xpMult: 1, pickup: null, pickupChance: 0, ...m,
});

/**
 * 20 role 的行為 + 掉落差異化。掉落設計(boss「不同敵人掉不同獎勵」)讓「打誰」有取捨:
 * - 支援型(healer/buffer/summoner)掉對應 pickup → 獎勵優先擊殺它們。
 * - 肉/精英掉多金幣 → 獎勵耐心清場。蟲群/偵察掉很少 → 純騷擾。
 */
export const ROLE_META: Record<EnemyRole, RoleMeta> = {
  grunt:       R({ attack: 'bolt',  coinMult: 1,   xpMult: 1 }),
  scout:       R({ attack: 'none',  coinMult: 0.6, xpMult: 1.3, pickup: 'magnet', pickupChance: 0.06 }),
  heavy:       R({ attack: 'shell', coinMult: 2.5, xpMult: 2,   pickup: 'coinburst', pickupChance: 0.18 }),
  'swarm-sine':R({ attack: 'bolt',  coinMult: 0.4, xpMult: 0.6 }),
  'swarm-chase':R({ attack: 'none', contact: true, coinMult: 0.4, xpMult: 0.6 }),
  sniper:      R({ attack: 'lance', coinMult: 1.5, xpMult: 1.8 }),
  bomber:      R({ attack: 'bomb',  coinMult: 1.4, xpMult: 1.6 }),
  shielder:    R({ attack: 'none',  hasShield: true, coinMult: 1.6, xpMult: 1.6 }),
  summoner:    R({ attack: 'none',  canSummon: true, coinMult: 3, xpMult: 2.5, pickup: 'coinburst', pickupChance: 0.5 }),
  buffer:      R({ attack: 'none',  buffsAllies: true, coinMult: 1.5, xpMult: 2, pickup: 'rage', pickupChance: 0.6 }),
  striker:     R({ attack: 'none',  contact: true, coinMult: 1,   xpMult: 1.2 }),
  spread:      R({ attack: 'bolt',  coinMult: 1.3, xpMult: 1.5 }),
  ring:        R({ attack: 'bolt',  coinMult: 1.3, xpMult: 1.5 }),
  beam:        R({ attack: 'beam',  coinMult: 1.5, xpMult: 1.8 }),
  homing:      R({ attack: 'missile', coinMult: 1.4, xpMult: 1.6 }),
  miner:       R({ attack: 'mine',  coinMult: 1.3, xpMult: 1.5 }),
  blinker:     R({ attack: 'bolt',  coinMult: 1.4, xpMult: 1.6 }),
  splitter:    R({ attack: 'bolt',  splitsOnDeath: true, coinMult: 1.2, xpMult: 1.3 }),
  healer:      R({ attack: 'none',  healsAllies: true, coinMult: 1.5, xpMult: 2, pickup: 'heal', pickupChance: 0.7 }),
  elite:       R({ attack: 'bolt',  canSummon: true, coinMult: 8, xpMult: 6, pickup: 'powerup', pickupChance: 1 }),
};

/** hp_tier(1-5)→ 基礎 HP(餵現有 enemyHpMult × waveStrengthHpMult 鏈)。對齊雜兵 basic≈12。 */
export const HP_BY_TIER: Record<number, number> = { 1: 6, 2: 12, 3: 22, 4: 40, 5: 75 };
/** speed → 水平速度(px/s,左向負;追逐型仍 < 玩家 280)。 */
export const SPEED_VX: Record<EnemyDef['speed'], number> = { slow: -90, med: -150, fast: -220 };

/** elemental 的能量彈/砲彈改用火焰皮(自然門 DoT 主題)。 */
export function projectileFor(def: EnemyDef): AttackKind {
  const a = ROLE_META[def.role].attack;
  if (def.faction === 'elemental' && (a === 'bolt' || a === 'shell')) return 'flame';
  return a;
}

const ALL: EnemyDef[] = (bestiary as { enemies: EnemyDef[] }).enemies;
const BY_ID = new Map<string, EnemyDef>(ALL.map((e) => [e.id, e]));
const BY_FACTION = new Map<FactionId, EnemyDef[]>();
for (const e of ALL) {
  const arr = BY_FACTION.get(e.faction) ?? [];
  arr.push(e);
  BY_FACTION.set(e.faction, arr);
}

export function allEnemyDefs(): readonly EnemyDef[] { return ALL; }
export function enemyDefById(id: string): EnemyDef | undefined { return BY_ID.get(id); }
export function enemiesForFaction(faction: FactionId): readonly EnemyDef[] { return BY_FACTION.get(faction) ?? []; }

// ── Sprite 幀(move/attack/damaged)key + URL ────────────────────────────────────
// boss 2026-06-17:每隻有 move(平常)/attack(開火瞬間)/damaged(血少)三幀。
// 透明 PNG 在 public/assets/ai-gen/enemies/<id>-<frame>.png(rembg/key 後)。
export type EnemyFrame = 'move' | 'attack' | 'damaged';
export const ENEMY_FRAMES: readonly EnemyFrame[] = ['move', 'attack', 'damaged'];
export function enemyFrameKey(id: string, frame: EnemyFrame): string { return `enemy-${id}-${frame}`; }
export function enemyFrameUrl(id: string, frame: EnemyFrame): string { return `assets/ai-gen/enemies/${id}-${frame}.png`; }

/** 某門派 20 隻 × 3 幀的 (key,url) 清單(scene preload 按門派載入,~60 張,輕量)。 */
export function enemyFrameLoadsForFaction(faction: FactionId): Array<{ key: string; url: string }> {
  const out: Array<{ key: string; url: string }> = [];
  for (const e of enemiesForFaction(faction)) {
    for (const f of ENEMY_FRAMES) out.push({ key: enemyFrameKey(e.id, f), url: enemyFrameUrl(e.id, f) });
  }
  return out;
}

/** 基礎 HP(未乘章難度/波次)= HP_BY_TIER[hp_tier]。 */
export function baseHpFor(def: EnemyDef): number { return HP_BY_TIER[def.hp_tier] ?? 12; }
export function vxFor(def: EnemyDef): number { return SPEED_VX[def.speed]; }

/** def.move → 現有 HEnemy 支援的 movePattern(未實作的 stationary/strafing/blink 暫退回)。 */
export function movePatternForDef(def: EnemyDef): 'straight' | 'sine' | 'diagonal' | 'chaseY' | 'chase' {
  switch (def.move) {
    case 'sine': case 'diagonal': case 'chaseY': case 'chase': case 'straight': return def.move;
    case 'strafing': return 'sine';
    default: return 'straight'; // stationary / blink:待真實作,暫直線
  }
}

/** def → 場景 EnemyTankType(餵現有 balance/fire/render fallback 的 basic/fast/tank)。 */
export function variantForDef(def: EnemyDef): 'basic' | 'fast' | 'tank' {
  if (def.hp_tier >= 4) return 'tank';
  if (def.speed === 'fast') return 'fast';
  return 'basic';
}

/** 該 role 是否為魔王/小魔王候選(Zoids 機械獸 hp_tier≥3,或 elite)。boss 2026-06-17。 */
export function isBossCandidate(def: EnemyDef): boolean {
  return def.role === 'elite' || (def.chassis === 'monster' && def.hp_tier >= 3);
}
export function bossCandidatesForFaction(faction: FactionId): EnemyDef[] {
  return enemiesForFaction(faction).filter(isBossCandidate);
}

// ── 波次抽選:前期雜兵多、後期重型/特殊、精英稀有且只在後段 ───────────────────────
/** role → {基礎權重, 最早出現的波次比例(0=第一波, 1=最後一波)}。 */
const ROLE_SPAWN: Record<EnemyRole, { weight: number; minFrac: number }> = {
  grunt:        { weight: 10, minFrac: 0 },
  scout:        { weight: 6,  minFrac: 0 },
  'swarm-sine': { weight: 7,  minFrac: 0 },
  'swarm-chase':{ weight: 6,  minFrac: 0.1 },
  striker:      { weight: 5,  minFrac: 0.2 },
  heavy:        { weight: 4,  minFrac: 0.25 },
  spread:       { weight: 4,  minFrac: 0.25 },
  sniper:       { weight: 3,  minFrac: 0.3 },
  bomber:       { weight: 3,  minFrac: 0.3 },
  ring:         { weight: 3,  minFrac: 0.4 },
  homing:       { weight: 3,  minFrac: 0.4 },
  miner:        { weight: 2,  minFrac: 0.4 },
  blinker:      { weight: 2,  minFrac: 0.5 },
  splitter:     { weight: 3,  minFrac: 0.4 },
  shielder:     { weight: 2,  minFrac: 0.5 },
  beam:         { weight: 2,  minFrac: 0.5 },
  summoner:     { weight: 2,  minFrac: 0.5 },
  buffer:       { weight: 2,  minFrac: 0.5 },
  healer:       { weight: 2,  minFrac: 0.5 },
  elite:        { weight: 1,  minFrac: 0.8 },
};

/**
 * 依 (門派, 波次) 加權抽一隻敵人。後段波解鎖更兇的 role 且權重隨進度上升。
 * rand: 0..1。純函式可單測。
 */
export function pickEnemyForWave(
  faction: FactionId, wave: number, totalWaves: number, rand: number,
): EnemyDef | undefined {
  const pool = enemiesForFaction(faction);
  if (!pool.length) return undefined;
  const frac = totalWaves > 1 ? Math.max(0, Math.min(1, wave / (totalWaves - 1))) : 0;
  const weighted: Array<{ def: EnemyDef; w: number }> = [];
  let total = 0;
  for (const def of pool) {
    const rs = ROLE_SPAWN[def.role];
    if (frac < rs.minFrac) continue;          // 還沒到該 role 出現的波段
    // 後段波讓「進階 role」(minFrac 高)權重上升,雜兵相對下降
    const ramp = 1 + (frac - rs.minFrac) * 1.5;
    const w = rs.weight * ramp;
    weighted.push({ def, w });
    total += w;
  }
  if (!weighted.length) return pool[0];
  let r = Math.max(0, Math.min(1, rand)) * total;
  for (const { def, w } of weighted) {
    r -= w;
    if (r <= 0) return def;
  }
  return weighted[weighted.length - 1].def;
}
